"use client";

import Image from "next/image";
import { Suspense } from "react";
import { ArrowRight, Sparkles, Download, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";

import { InteractiveShowcase } from "@/components/landing/interactive-showcase";
import { LandingPricingSection } from "@/components/landing/landing-pricing-section";
import { useLandingCta } from "@/hooks/use-landing-cta";

const GALLERY_ITEMS = [
  {
    title: "Sports Car",
    category: "Performance",
    before: "/showcase/images/before5.webp",
    after: "/showcase/images/after51.webp",
    description: "Stock to track-ready",
  },
  {
    title: "Sports Car",
    category: "Performance",
    before: "/showcase/images/before5.webp",
    after: "/showcase/images/after52.webp",
    description: "Aggressive concave design",
  },
  {
    title: "Sedan",
    category: "Daily Driver",
    before: "/showcase/images/before1.jpg",
    after: "/showcase/images/after11.webp",
    description: "Flow formed upgrade",
  },
  {
    title: "Sedan",
    category: "Daily Driver",
    before: "/showcase/images/before1.jpg",
    after: "/showcase/images/after12.webp",
    description: "Graphite mesh style",
  },
  {
    title: "Truck",
    category: "Off-Road",
    before: "/showcase/images/before6.webp",
    after: "/showcase/images/after61.webp",
    description: "Off-road ready",
  },
  {
    title: "Truck",
    category: "Off-Road",
    before: "/showcase/images/before6.webp",
    after: "/showcase/images/after62.webp",
    description: "Sport black finish",
  },
  {
    title: "SUV",
    category: "Luxury",
    before: "/showcase/images/before4.jpg",
    after: "/showcase/images/after41.webp",
    description: "Bronze forged wheels",
  },
  {
    title: "SUV",
    category: "Luxury",
    before: "/showcase/images/before4.jpg",
    after: "/showcase/images/after42.webp",
    description: "Aero-style design",
  },
];

const INTERACTIVE_SHOWCASES = [
  {
    name: "Sports Car Transformation",
    before: "/showcase/images/before5.webp",
    imageClassName: "object-[center_bottom]",
    wheels: [
      {
        id: "ba-1",
        name: "Track Style",
        image: "/showcase/images/after51.webp",
        thumbnail: "/showcase/images/after51.webp",
      },
      {
        id: "ba-2",
        name: "Concave",
        image: "/showcase/images/after52.webp",
        thumbnail: "/showcase/images/after52.webp",
      },
      {
        id: "ba-3",
        name: "Lightweight",
        image: "/showcase/images/after53.webp",
        thumbnail: "/showcase/images/after53.webp",
      },
    ],
  },
  {
    name: "Truck Transformation",
    before: "/showcase/images/before6.webp",
    imageClassName: "object-[center_bottom]",
    wheels: [
      {
        id: "ba-4",
        name: "Off-Road",
        image: "/showcase/images/after61.webp",
        thumbnail: "/showcase/images/after61.webp",
      },
      {
        id: "ba-5",
        name: "Sport Black",
        image: "/showcase/images/after62.webp",
        thumbnail: "/showcase/images/after62.webp",
      },
      {
        id: "ba-6",
        name: "Chrome",
        image: "/showcase/images/after63.webp",
        thumbnail: "/showcase/images/after63.webp",
      },
    ],
  },
];

export function BeforeAndAfterWheelsContent() {
  const { showStickyCta, ctaText, handleTryItNow } = useLandingCta();

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Before and After Wheels",
    url: "https://thewheelvisualizer.com/before-and-after-wheels",
    description:
      "Browse wheel upgrade transformations. See real before and after comparisons.",
    mainEntity: {
      "@type": "ImageGallery",
      name: "Wheel Transformation Gallery",
      description: "Collection of before and after wheel upgrade comparisons",
    },
  };

  return (
    <div className="bg-[#04060A] text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      {/* Hero - Gallery focused */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6">Gallery</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            Before & After
            <br />
            <span className="text-white/60">Wheel Transformations</span>
          </h1>
          <p className="text-xl text-white/60 leading-relaxed max-w-2xl mb-8">
            See how new wheels completely change a car&apos;s look. Browse real transformations and get inspired for your own upgrade.
          </p>
          <Button
            size="lg"
            onClick={handleTryItNow}
            className="bg-white text-black hover:bg-white/90 font-semibold h-12 px-8"
          >
            Create Your Own
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Stats bar */}
      <section className="py-8 px-6 border-y border-white/5 bg-white/[0.01]">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap justify-center gap-12 text-center">
            <div>
              <div className="text-2xl font-semibold text-white">All AI-Generated</div>
              <div className="text-sm text-white/50">No physical mounting</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-white">HD Quality</div>
              <div className="text-sm text-white/50">Downloadable results</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-white">Under 60s</div>
              <div className="text-sm text-white/50">Per transformation</div>
            </div>
          </div>
        </div>
      </section>

      {/* Grid gallery */}
      <section className="py-24 px-6 bg-[#080A0D]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Transformations</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Side-by-side comparisons
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {GALLERY_ITEMS.map((item, idx) => (
              <div
                key={idx}
                className="group rounded-2xl overflow-hidden border border-white/5 bg-white/[0.02] hover:border-white/10 transition-colors"
              >
                <div className="grid grid-cols-2">
                  {/* Before */}
                  <div className="relative">
                    <div className="aspect-[4/3] relative">
                      <Image
                        src={item.before}
                        alt={`${item.title} before`}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 50vw, 25vw"
                      />
                    </div>
                    <div className="absolute top-3 left-3 px-2 py-0.5 bg-black/70 backdrop-blur-sm rounded-full text-[10px] font-medium text-white/80 uppercase tracking-wide">
                      Before
                    </div>
                  </div>

                  {/* After */}
                  <div className="relative border-l border-white/5">
                    <div className="aspect-[4/3] relative">
                      <Image
                        src={item.after}
                        alt={`${item.title} after`}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 50vw, 25vw"
                      />
                    </div>
                    <div className="absolute top-3 left-3 px-2 py-0.5 bg-white/90 backdrop-blur-sm rounded-full text-[10px] font-medium text-black uppercase tracking-wide">
                      After
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-white/40 uppercase tracking-wide">{item.category}</p>
                      <p className="text-white/80 mt-1">{item.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive showcases */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Interactive</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Drag to compare
            </h2>
            <p className="text-white/60 mt-4">
              Use the slider to see the transformation. Tap thumbnails to try different wheel styles.
            </p>
          </div>

          <div className="space-y-16">
            {INTERACTIVE_SHOWCASES.map((showcase, idx) => (
              <div key={showcase.name}>
                <p className="text-sm text-white/50 mb-4">{showcase.name}</p>
                <InteractiveShowcase
                  beforeImage={showcase.before}
                  wheels={showcase.wheels}
                  imageClassName={showcase.imageClassName}
                  priority={idx === 0}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits of visualization */}
      <section className="py-24 px-6 bg-[#080A0D] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-5 text-white/70">
                <Sparkles className="h-5 w-5" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">No guesswork</h3>
              <p className="text-white/60 leading-relaxed">
                See exactly how wheels will look before spending money. Make decisions with visual proof.
              </p>
            </div>
            <div>
              <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-5 text-white/70">
                <Download className="h-5 w-5" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Download HD</h3>
              <p className="text-white/60 leading-relaxed">
                Every transformation is downloadable in full resolution. No watermarks, no restrictions.
              </p>
            </div>
            <div>
              <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-5 text-white/70">
                <Share2 className="h-5 w-5" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Share easily</h3>
              <p className="text-white/60 leading-relaxed">
                Send to friends, post on forums, or show your wheel shop exactly what you want.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
            Create your own transformation
          </h2>
          <p className="text-white/60 mb-8">
            Upload your car photo and see how new wheels will look. Takes less than a minute.
          </p>
          <Button
            size="lg"
            onClick={handleTryItNow}
            className="bg-white text-black hover:bg-white/90 font-semibold h-12 px-8"
          >
            {ctaText}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <Suspense fallback={<div className="py-24 px-6 bg-[#0A0C0F]" />}>
        <LandingPricingSection />
      </Suspense>

      {/* Sticky CTA */}
      {showStickyCta && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#05070C]/95 backdrop-blur border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 py-3">
            <button
              onClick={handleTryItNow}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-white text-black font-semibold"
            >
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
